import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { track, trackError } from '@/lib/analytics'
import { emitPollEvent } from '@/api/pollEvents'
import {
  checkChannelBotRights,
  createPoll,
  publishPoll,
  type ChannelRightsResponse,
  type CreatePollPayload,
  type PollType,
} from '@/api/polls'
import { PollsWizardShell } from '@/onboarding/PollsWizardShell'
import { usePollsWizard } from '@/onboarding/PollsWizardContext'
import { useChannel } from '@/context/ChannelContext'
import { BotRightsCheckModal } from '@/polls/BotRightsCheckModal'
import { BotRightsResolvedModal } from '@/polls/BotRightsResolvedModal'

const QUESTION_MAX = 300
const OPTION_MAX = 100
const DESCRIPTION_MAX = 500 // addendum 2026-07-24, длиннее чем вопрос

/**
 * Slide 19 в HTML (шаг 2/3 wizard'а) · «Редактор + превью».
 * Full editor: type toggle · вопрос · options list с add/remove · quiz mark ·
 * флаги (анонимный / закрыть 24ч) + live-preview MAX-поста справа.
 * Design ref: design_v2_pool/onboarding.html slide 19 (2026-07-27, обновлён).
 */
export function PollsWizardPreviewPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const {
    state,
    setType,
    setQuestion,
    setDescription,
    setOptionText,
    addOption,
    removeOption,
    setCorrectIndex,
    setAnonymous,
    setCloseIn24h,
    setChannelUUID,
  } = usePollsWizard()
  const { selectedChannel, channels } = useChannel()
  const [publishing, setPublishing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showRightsReadyModal, setShowRightsReadyModal] = useState(false)
  const [botRightsModal, setBotRightsModal] = useState<{ open: boolean; seed: ChannelRightsResponse | null }>({
    open: false,
    seed: null,
  })

  const channelUUID = state.channelUUID ?? params.get('channelUUID') ?? selectedChannel?.uuid ?? null
  const ref = state.ref ?? params.get('ref')

  useEffect(() => {
    if (!state.channelUUID && selectedChannel?.uuid) {
      setChannelUUID(selectedChannel.uuid)
    }
  }, [selectedChannel?.uuid, setChannelUUID, state.channelUUID])

  // Гвард: без templateId → возвращаемся на шаг 1 (юзер зашёл прямо через URL).
  useEffect(() => {
    if (!state.templateId) {
      const qs = new URLSearchParams()
      if (channelUUID) qs.set('channelUUID', channelUUID)
      if (ref) qs.set('ref', ref)
      navigate(`/onboarding/polls/template?${qs.toString()}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.templateId])

  const questionCharCount = useMemo(() => Array.from(state.question).length, [state.question])
  const descriptionCharCount = useMemo(() => Array.from(state.description).length, [state.description])
  const validOptions = state.options.filter((o) => o.text.trim().length > 0)
  const canPublish =
    !!channelUUID &&
    state.question.trim().length > 0 &&
    questionCharCount <= QUESTION_MAX &&
    descriptionCharCount <= DESCRIPTION_MAX &&
    validOptions.length >= 2 &&
    state.options.every((o) => Array.from(o.text).length <= OPTION_MAX) &&
    (state.type !== 'quiz' || validOptions.some((o) => o.isCorrect))

  const previewChannel = channels.find((c) => c.uuid === channelUUID) ?? selectedChannel ?? null

  const handlePublish = async () => {
    if (!canPublish || publishing || !channelUUID) return
    setPublishing(true)
    setError(null)
    setShowRightsReadyModal(false)
    const rights = await checkChannelBotRights(channelUUID)
    if (rights && rights.missing.length > 0) {
      setPublishing(false)
      setBotRightsModal({ open: true, seed: rights })
      return
    }
    try {
      emitPollEvent('poll_onboarding_step_completed', {
        pollId: ref ?? undefined,
        meta: {
          source: 'lk',
          step: 'preview_confirmed',
          ref: ref ?? null,
          channelUUID,
          template: state.templateId,
          type: state.type,
        },
      })

      const trimmedDescription = state.description.trim()
      const payload: CreatePollPayload = {
        channelUUID,
        type: state.type,
        question: state.question.trim(),
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        options: validOptions.map((o) => ({
          text: o.text.trim(),
          ...(state.type === 'quiz' ? { isCorrect: !!o.isCorrect } : {}),
        })),
        settings: {
          anonymous: state.anonymous,
          showResultsBeforeClose: true,
          allowRevote: true,
          pinToChannel: false,
          maxOptionsPerUser: state.type === 'multi' ? validOptions.length : null,
        },
        schedule: {
          publishMode: 'immediate',
          closeMode: state.closeIn24h ? 'in_1d' : 'manual',
        },
      }

      const created = await createPoll(payload)
      if (!created) {
        setError('Не удалось создать опрос. Попробуйте ещё раз.')
        trackError(new Error('createPoll null (wizard)'), 'polls-wizard-create')
        return
      }
      const published = await publishPoll(created.pollId)
      if (!published) {
        setError('Не удалось опубликовать опрос. Попробуйте ещё раз.')
        trackError(new Error('publishPoll null (wizard)'), 'polls-wizard-publish')
        return
      }
      if ('code' in published) {
        if (published.code === 'BOT_MISSING_RIGHTS') {
          setBotRightsModal({ open: true, seed: null })
          return
        }
        return
      }
      track('poll_wizard_published', { pollId: published.pollId, type: state.type, template: state.templateId, ref })
      const qs = new URLSearchParams({ pollId: published.pollId })
      if (ref) qs.set('ref', ref)
      navigate(`/onboarding/polls/success?${qs.toString()}`, { replace: true })
    } finally {
      setPublishing(false)
    }
  }

  return (
    <PollsWizardShell
      step={2}
      total={3}
      footer={
        <>
          <button
            className="pw-btn pw-btn-ghost"
            type="button"
            onClick={() => navigate('/onboarding/polls/template', { replace: true })}
          >
            ← Назад
          </button>
          <div className="pw-foot-spacer" />
          {error && <span className="pw-error">{error}</span>}
          <button
            className="pw-btn pw-btn-primary"
            type="button"
            onClick={handlePublish}
            disabled={!canPublish || publishing}
          >
            {publishing ? 'Публикуем…' : 'Опубликовать в канал →'}
          </button>
        </>
      }
    >
      <div className="pw-editor-grid">
        <div className="pw-editor-col">
          <p className="pw-eyebrow-line">
            <span className="pw-eyebrow-pill">Опросы</span>
            Проверьте и&nbsp;публикуйте
          </p>
          <h1 className="pw-title">
            Так&nbsp;опрос увидят <em>в&nbsp;канале</em>.
          </h1>
          <p className="pw-sub">
            Можно поправить тип, вопрос и&nbsp;варианты. Всё живо в&nbsp;превью справа.
          </p>

          <TypeToggle type={state.type} onChange={setType} />

          <label className="pw-field">
            <span className="pw-field-label">Вопрос</span>
            <input
              className="pw-input"
              type="text"
              value={state.question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={QUESTION_MAX + 20}
            />
            <span className="pw-field-hint">
              {questionCharCount} / {QUESTION_MAX}
            </span>
          </label>

          <label className="pw-field">
            <span className="pw-field-label">
              Описание <span className="pw-optional">(необязательно)</span>
            </span>
            <textarea
              className="pw-input pw-textarea"
              placeholder="Дополнительный контекст, ссылка на источник, пояснение — если одного вопроса мало"
              value={state.description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={DESCRIPTION_MAX + 40}
              rows={3}
            />
            <span className={`pw-field-hint ${descriptionCharCount > DESCRIPTION_MAX ? 'warn' : ''}`}>
              Покажем мелким шрифтом под вопросом · {descriptionCharCount} / {DESCRIPTION_MAX}
            </span>
          </label>

          <div className="pw-field">
            <span className="pw-field-label">Варианты ответа</span>
            <div className="opts">
              {state.options.map((opt, i) => (
                <div className="opt-row" key={i}>
                  {state.type === 'quiz' && (
                    <button
                      type="button"
                      className={`quiz-mark ${opt.isCorrect ? 'on' : ''}`}
                      onClick={() => setCorrectIndex(i)}
                      aria-label="Правильный ответ"
                      title="Правильный ответ"
                    >
                      {opt.isCorrect && (
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      )}
                    </button>
                  )}
                  <input
                    className="pw-input opt-input"
                    type="text"
                    value={opt.text}
                    onChange={(e) => setOptionText(i, e.target.value)}
                    placeholder={`Вариант ${String.fromCharCode(0x0410 + i)}`}
                    maxLength={OPTION_MAX + 20}
                  />
                  <button
                    type="button"
                    className="opt-del"
                    onClick={() => removeOption(i)}
                    disabled={state.options.length <= 2}
                    aria-label="Удалить вариант"
                    title="Удалить вариант"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="opt-add"
              onClick={addOption}
              disabled={state.options.length >= 8}
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Добавить вариант
            </button>
          </div>

          <div className="pw-toggles">
            <label className="pw-check">
              <input
                type="checkbox"
                checked={state.anonymous}
                onChange={(e) => setAnonymous(e.target.checked)}
              />
              Анонимный
            </label>
            <label className="pw-check">
              <input
                type="checkbox"
                checked={state.closeIn24h}
                onChange={(e) => setCloseIn24h(e.target.checked)}
              />
              Закрыть через&nbsp;24&nbsp;ч
            </label>
          </div>
        </div>

        <div className="pw-preview-col">
          <MaxPostPreview
            channelTitle={previewChannel?.title ?? 'Ваш канал'}
            question={state.question}
            description={state.description}
            options={state.options}
            type={state.type}
            anonymous={state.anonymous}
          />
        </div>
      </div>
      <PageStyles />
      {botRightsModal.open && channelUUID && (
        <BotRightsCheckModal
          channelUUID={channelUUID}
          channelTitle={previewChannel?.title ?? 'канал'}
          seedRights={botRightsModal.seed}
          onCancel={() => setBotRightsModal({ open: false, seed: null })}
          onSatisfied={() => {
            setBotRightsModal({ open: false, seed: null })
            setShowRightsReadyModal(true)
          }}
        />
      )}
      {showRightsReadyModal && (
        <BotRightsResolvedModal onDone={() => setShowRightsReadyModal(false)} />
      )}
    </PollsWizardShell>
  )
}

function TypeToggle({ type, onChange }: { type: PollType; onChange: (t: PollType) => void }) {
  const hint =
    type === 'quiz'
      ? 'Один вариант правильный — отметьте его галочкой слева.'
      : type === 'multi'
      ? 'Подписчик может отметить несколько вариантов.'
      : 'Подписчик выбирает один вариант.'
  return (
    <div className="pw-field">
      <span className="pw-field-label">Тип опроса</span>
      <div className="ptype-toggle">
        {[
          { id: 'single' as const, label: 'Один ответ' },
          { id: 'multi' as const, label: 'Несколько' },
          { id: 'quiz' as const, label: 'Викторина' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            className={`ptype ${type === t.id ? 'on' : ''}`}
            onClick={() => onChange(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p className="ptype-hint">{hint}</p>
    </div>
  )
}

function MaxPostPreview({
  channelTitle,
  question,
  description,
  options,
  type,
  anonymous,
}: {
  channelTitle: string
  question: string
  description: string
  options: { text: string; isCorrect?: boolean }[]
  type: PollType
  anonymous: boolean
}) {
  const initials = channelTitle
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
  const meta =
    type === 'quiz'
      ? `Викторина${anonymous ? ' · анонимно' : ''} · 0 ответов`
      : type === 'multi'
      ? `Несколько ответов${anonymous ? ' · анонимно' : ''} · 0 голосов`
      : `Один ответ${anonymous ? ' · анонимно' : ''} · 0 голосов`
  return (
    <div className="max-post">
      <div className="mp-head">
        <div className="mp-avatar" aria-hidden>{initials || '??'}</div>
        <div>
          <div className="mp-title">{channelTitle}</div>
          <div className="mp-sub">только что · бот</div>
        </div>
      </div>
      <div className="mp-body">
        <div className="mp-q">{question || 'Ваш вопрос…'}</div>
        {description.trim() && <div className="mp-desc">{description}</div>}
        <div className="mp-opts">
          {options.map((o, i) => {
            const isCorrect = type === 'quiz' && !!o.isCorrect
            return (
              <div key={i} className={`mp-opt ${isCorrect ? 'correct' : ''}`}>
                <span className={`mp-mark mp-mark-${type} ${isCorrect ? 'on' : ''}`}>
                  {isCorrect && (
                    <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </span>
                <span className="mp-opt-text">
                  {o.text || `Вариант ${String.fromCharCode(0x0410 + i)}`}
                </span>
              </div>
            )
          })}
        </div>
        <div className="mp-meta">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-12V5l-8-3-8 3v5c0 8 8 12 8 12z" />
          </svg>
          {meta}
        </div>
      </div>
    </div>
  )
}

function PageStyles() {
  return (
    <style>{`
      .pw-editor-grid {
        display: grid; grid-template-columns: minmax(0, 1fr) minmax(320px, 380px); gap: 40px;
        align-items: start;
        width: 100%;
        min-width: 0;
      }
      @media (max-width: 900px) { .pw-editor-grid { grid-template-columns: 1fr; } }
      .pw-editor-col { max-width: 520px; width: 100%; min-width: 0; }

      .pw-eyebrow-line {
        display: flex; align-items: center; gap: 10px;
        font-size: 12.5px; color: var(--tg-mute, #8f9eb0); font-weight: 500;
        margin: 0 0 14px;
      }
      .pw-eyebrow-pill {
        display: inline-flex; padding: 4px 10px; border-radius: 999px;
        background: var(--tg-blue-bg, #ebf2ff); color: var(--tg-blue-press, #0052cc);
        font-weight: 700; font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase;
      }
      .pw-title {
        font-weight: 800; font-size: 32px; line-height: 1.05;
        letter-spacing: -0.026em; color: var(--tg-ink, #111);
        margin: 0 0 8px;
      }
      .pw-title em { font-style: italic; font-weight: 400; color: var(--tg-blue, #0066ff); }
      .pw-sub { font-size: 14px; color: var(--tg-mute, #8f9eb0); margin: 0 0 22px; line-height: 1.5; }

      .pw-field { display: block; margin-bottom: 20px; }
      .pw-field-label {
        display: block; font-size: 12px; font-weight: 700; letter-spacing: 0.04em;
        text-transform: uppercase; color: var(--tg-mute, #8f9eb0); margin-bottom: 8px;
      }

      .ptype-toggle {
        display: inline-flex; gap: 3px; padding: 3px;
        background: var(--tg-surface-2, #f5f5f9); border-radius: 11px;
        max-width: 100%;
        overflow-x: auto;
        scrollbar-width: none;
      }
      .ptype-toggle::-webkit-scrollbar { display: none; }
      .ptype {
        border: 0; padding: 8px 14px; border-radius: 9px;
        font-family: inherit; font-size: 13px; font-weight: 600;
        color: var(--tg-mute, #8f9eb0); background: transparent; cursor: pointer;
        flex: 0 0 auto;
        white-space: nowrap;
      }
      .ptype:hover { color: var(--tg-ink-2, #333); }
      .ptype.on {
        background: var(--tg-surface, #fff); color: var(--tg-ink, #111);
        box-shadow: 0 1px 3px rgba(38,40,45,0.1);
      }
      .ptype-hint { font-size: 12.5px; color: var(--tg-mute, #8f9eb0); margin: 9px 0 0; }

      .pw-input {
        width: 100%; padding: 11px 13px;
        border: 1px solid var(--tg-line-2, #f0f0f4); border-radius: 10px;
        font-family: inherit; font-size: 14px; color: var(--tg-ink, #111);
        background: var(--tg-surface, #fff);
        box-sizing: border-box;
        min-width: 0;
      }
      .pw-input:focus { outline: 2px solid var(--tg-blue-soft, rgba(0,128,255,0.35)); }
      .pw-field-hint {
        display: block; font-size: 11.5px; color: var(--tg-mute, #8f9eb0);
        margin-top: 4px; text-align: right; font-variant-numeric: tabular-nums;
      }
      .pw-field-hint.warn { color: #dc3545; }
      .pw-optional {
        font-weight: 500; color: var(--tg-mute, #8f9eb0);
        text-transform: none; letter-spacing: 0; margin-left: 4px;
      }
      .pw-textarea {
        min-height: 72px; resize: vertical;
        font-family: inherit; line-height: 1.45;
      }

      .opts { display: flex; flex-direction: column; gap: 8px; }
      .opt-row { display: flex; align-items: center; gap: 8px; min-width: 0; }
      .opt-input { flex: 1; padding: 10px 13px; min-width: 0; }
      .opt-del {
        width: 32px; height: 32px; flex-shrink: 0;
        border: 0; background: transparent; color: var(--tg-mute, #8f9eb0);
        cursor: pointer; border-radius: 8px;
        display: flex; align-items: center; justify-content: center;
      }
      .opt-del:hover:not(:disabled) { background: var(--tg-surface-2, #f5f5f9); color: #dc3545; }
      .opt-del:disabled { opacity: 0.3; cursor: default; }
      .quiz-mark {
        width: 22px; height: 22px; flex-shrink: 0;
        border-radius: 50%; border: 2px solid var(--tg-line-3, #d8d8de);
        background: transparent; cursor: pointer; padding: 0;
        display: flex; align-items: center; justify-content: center;
      }
      .quiz-mark.on {
        border-color: var(--tg-up, #17a06a);
        background: var(--tg-up, #17a06a); color: #fff;
      }
      .opt-add {
        margin-top: 9px;
        display: inline-flex; align-items: center; gap: 7px;
        border: 1px dashed var(--tg-line-3, #d8d8de); background: transparent;
        border-radius: 10px; padding: 9px 14px;
        font-family: inherit; font-size: 13.5px; font-weight: 600;
        color: var(--tg-blue, #0066ff); cursor: pointer;
      }
      .opt-add:hover:not(:disabled) { background: var(--tg-blue-bg, #ebf2ff); border-color: var(--tg-blue-soft, rgba(0,128,255,0.4)); }
      .opt-add:disabled { opacity: 0.4; cursor: default; }

      .pw-toggles { display: flex; gap: 22px; flex-wrap: wrap; }
      .pw-check {
        display: inline-flex; align-items: center; gap: 8px;
        font-size: 13.5px; color: var(--tg-ink-2, #333); cursor: pointer;
      }
      .pw-check input[type="checkbox"] {
        width: 16px; height: 16px; accent-color: var(--tg-blue, #0066ff);
      }

      .pw-error { color: #dc3545; font-size: 13px; }

      .pw-preview-col { position: sticky; top: 20px; min-width: 0; }
      .max-post {
        background: #fff; border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 16px; overflow: hidden;
        box-shadow: 0 24px 60px -16px rgba(38,40,45,0.12);
      }
      .mp-head {
        display: flex; align-items: center; gap: 10px;
        padding: 14px 16px; border-bottom: 1px solid var(--tg-line, #e8e8ec);
      }
      .mp-avatar {
        width: 38px; height: 38px; border-radius: 50%;
        background: linear-gradient(135deg, #8B6C4E, #5C4433);
        display: flex; align-items: center; justify-content: center;
        color: #fff; font-weight: 700; font-size: 13px; letter-spacing: 0.02em;
      }
      .mp-title { font-weight: 700; font-size: 14px; color: var(--tg-ink, #111); }
      .mp-sub { font-size: 12px; color: var(--tg-mute, #8f9eb0); }
      .mp-body { padding: 16px; }
      .mp-q { font-size: 15px; font-weight: 600; color: var(--tg-ink, #111); line-height: 1.4; margin-bottom: 8px; }
      .mp-desc {
        font-size: 12.5px; color: var(--tg-mute, #8f9eb0);
        line-height: 1.5; margin-bottom: 14px; white-space: pre-wrap;
      }
      .mp-opts { display: flex; flex-direction: column; gap: 8px; }
      .mp-opt {
        display: flex; align-items: center; gap: 10px;
        border: 1.5px solid var(--tg-line-2, #f0f0f4); border-radius: 10px;
        padding: 11px 13px; font-size: 14px; color: var(--tg-ink-2, #333);
      }
      .mp-opt.correct { border-color: var(--tg-up, #17a06a); background: rgba(23,160,106,0.06); }
      .mp-opt-text { flex: 1; min-width: 0; }
      .mp-mark {
        width: 16px; height: 16px; flex-shrink: 0;
        border: 1.5px solid var(--tg-line-3, #d8d8de);
        display: flex; align-items: center; justify-content: center;
      }
      .mp-mark-single { border-radius: 50%; }
      .mp-mark-quiz { border-radius: 50%; }
      .mp-mark-multi { border-radius: 4px; }
      .mp-mark.on { background: var(--tg-up, #17a06a); border-color: var(--tg-up, #17a06a); color: #fff; }
      .mp-meta {
        margin-top: 12px; font-size: 12px; color: var(--tg-mute, #8f9eb0);
        display: flex; align-items: center; gap: 6px;
      }
      @media (max-width: 700px) {
        .pw-title { font-size: 28px; }
        .pw-sub { margin-bottom: 18px; }
        .pw-eyebrow-line { flex-wrap: wrap; gap: 8px; }
        .ptype-toggle { width: 100%; }
        .pw-preview-col { position: static; }
      }
    `}</style>
  )
}
